import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';

import { ConfigSwitchSection } from '../../../common';
import { SupportedCountriesValue, Country } from '../../../store/modules/merchant/types';
import { useGetSupportedCountriesQuery } from '../../../store/modules/merchant/api';

interface Props {
  supportedCountriesValue?: SupportedCountriesValue;
  onChangeSupportedCountries: (supportedCountries: SupportedCountriesValue) => void;
  isEnabled: boolean;
  onToggleEnabled: (isEnabled: boolean) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export const CrossBorderPayments = ({
  supportedCountriesValue = { countries: ['US'], supportAll: false },
  isEnabled = false,
  isLoading = false,
  onChangeSupportedCountries,
  onToggleEnabled,
  disabled,
}: Props) => {
  const { data: countries = [], isLoading: isLoadingCountries } = useGetSupportedCountriesQuery();
  const { supportAll, countries: supportedCountries = [] } = supportedCountriesValue;

  const countryList = useMemo(() => {
    const filtered = countries.filter(({ countryCode }) => countryCode !== 'US');
    filtered.sort((a, b) => a.countryName.localeCompare(b.countryName));
    return filtered;
  }, [countries]);

  const selectedCountries = useMemo(() => {
    if (supportAll) {
      return countryList;
    }
    return countryList.filter((country) => supportedCountries.includes(country.countryCode));
  }, [supportAll, supportedCountries, countryList]);

  const handleCountrySelection = (selected: Country[]) => {
    const selectedCodes = selected.map((c) => c.countryCode);
    const countriesSet = new Set(selectedCodes);
    countriesSet.add('US');

    onChangeSupportedCountries({ countries: Array.from(countriesSet), supportAll: false });
  };

  const handleRemoveCountry = (countryCode: string) => {
    if (supportAll) {
      onChangeSupportedCountries({
        supportAll: false,
        countries: countries.map((c) => c.countryCode).filter((country) => country !== countryCode),
      });
      return;
    }

    const countriesSet = new Set(supportedCountries);
    countriesSet.add('US');
    countriesSet.delete(countryCode);
    if (countriesSet.size === 1 && countriesSet.has('US')) {
      countriesSet.clear();
    }

    onChangeSupportedCountries({ countries: Array.from(countriesSet), supportAll: false });
  };

  return (
    <Stack sx={{ maxWidth: '450px' }} spacing={2}>
      <ConfigSwitchSection
        title="Allow Cross Border Payments"
        description="When you turn on cross border payments, non-US shoppers are able to pay US merchants using non-US billing addresses."
        isChecked={isEnabled}
        onToggle={(isChecked) => onToggleEnabled(isChecked)}
        isLoading={isLoading}
        switchLabelTitle="Cross Border Payments"
        disabled={disabled}
      />
      {isEnabled && (
        <>
          <Typography sx={{ fontSize: '12px', color: 'black' }}>
            Select the non-US countries to accept non-US transactions from (US will always be
            enabled)
          </Typography>
          {isLoadingCountries ? (
            Array.from({ length: 4 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={index} variant="rounded" height={16} />
            ))
          ) : (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!supportAll}
                    onChange={(e) => onChangeSupportedCountries({ supportAll: e.target.checked })}
                    disabled={disabled}
                  />
                }
                label="Select All"
                sx={{
                  mb: 1,
                  '& span': {
                    fontSize: 14,
                    color: 'black',
                  },
                }}
              />
              <Autocomplete
                multiple
                disabled={disabled || supportAll}
                options={countryList}
                getOptionLabel={(option) => option.countryName}
                value={selectedCountries}
                onChange={(_e, newValue) => handleCountrySelection(newValue)}
                isOptionEqualToValue={(option, value) => option.countryCode === value.countryCode}
                renderOption={(props: { key: string }, option, { selected }) => {
                  const { key, ...rest } = props;
                  return (
                    <li
                      key={key}
                      {...rest}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{option.countryName}</span>
                      {(selected || supportAll) && (
                        <Check sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select countries"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                      },
                    }}
                  />
                )}
                renderTags={() => null}
                sx={{ mb: selectedCountries.length > 0 ? 1 : 0 }}
              />
              {selectedCountries.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {selectedCountries.map((country) => (
                    <Chip
                      key={country.countryCode}
                      label={country.countryName}
                      onDelete={() => handleRemoveCountry(country.countryCode)}
                      deleteIcon={<Close sx={{ fontSize: 16 }} />}
                      disabled={disabled}
                      sx={{
                        fontSize: 12,
                        '& .MuiChip-deleteIcon': {
                          fontSize: 16,
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};
